import React, { useEffect, useState } from "react";
import { Download, ImageIcon, Loader2, AlertTriangle } from "lucide-react";
import api from "@/lib/api";

/**
 * Authenticated preview + download for a support-ticket screenshot.
 *
 * The attachment bytes live behind an authenticated endpoint (see
 * ``/api/support/tickets/{id}/attachment`` for customers and
 * ``/api/admin/support/tickets/{id}/attachment`` for admins). We fetch the
 * blob via the shared axios client (which forwards the session cookie +
 * bearer token) and expose it as an object URL so the browser can render
 * it inline without ever putting the bytes on a public path.
 *
 * Props:
 *   apiPath   - full "/support/..." or "/admin/support/..." path (no host).
 *   filename  - display name (also used for the download attribute).
 *   sizeBytes - optional, displayed in the caption.
 *   testId    - data-testid prefix.
 */
export default function SupportAttachmentViewer({
  apiPath,
  filename,
  sizeBytes,
  testId = "support-attachment",
}) {
  const [blobUrl, setBlobUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let revokeUrl = null;
    let cancelled = false;
    setLoading(true);
    setError(null);
    setBlobUrl(null);
    api
      .get(apiPath, { responseType: "blob" })
      .then((res) => {
        if (cancelled) return;
        const url = URL.createObjectURL(res.data);
        revokeUrl = url;
        setBlobUrl(url);
      })
      .catch((err) => {
        if (cancelled) return;
        const status = err?.response?.status;
        setError(
          status === 404
            ? "Attachment not found."
            : status === 403
              ? "You don't have access to this attachment."
              : "Could not load the attachment.",
        );
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
      if (revokeUrl) URL.revokeObjectURL(revokeUrl);
    };
  }, [apiPath]);

  const prettySize =
    typeof sizeBytes === "number" && sizeBytes > 0
      ? sizeBytes >= 1024 * 1024
        ? `${(sizeBytes / (1024 * 1024)).toFixed(1)} MB`
        : `${Math.max(1, Math.round(sizeBytes / 1024))} KB`
      : null;

  return (
    <div
      className="rounded-md border border-[color:var(--border-subtle)] bg-[color:var(--surface-2)] p-2.5"
      data-testid={`${testId}-wrapper`}
    >
      <div className="flex items-center gap-2 text-[11px] text-[color:var(--text-3)]">
        <ImageIcon className="h-3.5 w-3.5" />
        <span className="font-mono text-[color:var(--text-2)] break-all" data-testid={`${testId}-filename`}>
          {filename || "screenshot"}
        </span>
        {prettySize ? <span>· {prettySize}</span> : null}
        {blobUrl ? (
          <a
            href={blobUrl}
            download={filename || "screenshot"}
            className="ml-auto inline-flex items-center gap-1 text-[color:var(--brand-primary)] hover:underline"
            data-testid={`${testId}-download`}
          >
            <Download className="h-3.5 w-3.5" />
            Download
          </a>
        ) : null}
      </div>
      <div className="mt-2 flex items-center justify-center rounded border border-[color:var(--border-subtle)] bg-[color:var(--bg-0)] min-h-[120px] max-h-[420px] overflow-hidden">
        {loading ? (
          <div className="flex items-center gap-2 text-[11px] text-[color:var(--text-3)] py-4" data-testid={`${testId}-loading`}>
            <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading preview…
          </div>
        ) : error ? (
          <div className="flex items-center gap-2 text-[11px] text-[color:var(--accent-warm)] py-4" data-testid={`${testId}-error`}>
            <AlertTriangle className="h-3.5 w-3.5" /> {error}
          </div>
        ) : blobUrl ? (
          <img
            src={blobUrl}
            alt={filename || "Support screenshot"}
            className="max-h-[420px] w-auto object-contain"
            data-testid={`${testId}-image`}
          />
        ) : null}
      </div>
    </div>
  );
}
